import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import FormItem from '../../../components/form/FormItem'
import { MdTitle, MdPlaylistAdd } from 'react-icons/md'
import { BiX, BiTrash } from 'react-icons/bi'
import { Button, Form, Card, CardBody, CardHeader } from 'reactstrap'
import { fetchFormElementGet } from '../../../redux/slices/formElementGetSlice'
import { fetchFormElementCreate } from '../../../redux/slices/formElementCreateSlice'
import { fetchFormElementUpdate } from '../../../redux/slices/formElementUpdateSlice'
import SelectItem from '../../../components/form/SelectItem'
import { FormTypes } from '../../../types'

const CrudFormElements = ({ showMenu, setShowMenu, formGroupId, languages, setChanged, setSelectedItem, selectedItem, t }) => {
    const initialFormData = { files: [], type: 1, formId: formGroupId };
    const [formData, setFormData] = useState(initialFormData);
    const [formGroups, setFormGroups] = useState([{ ...initialFormData, id: Date.now() }]);
    const dispatch = useDispatch();

    const getData = async () => {
        if (selectedItem !== 0) {
            var data = await dispatch(fetchFormElementGet({ id: selectedItem }))
            if (data.payload) {
                var value = data.payload?.element
                var newFormData = { ...formData }
                languages.forEach(item => {
                    newFormData[`element${item.code}`] = value[item.code]
                });
                setFormData(newFormData)
            }
        }
    }

    const addNewGroup = () => {
        setFormGroups([...formGroups, { ...initialFormData, id: Date.now() }]);
    };

    const updateFormGroup = (id, field, value) => {
        const updatedGroups = formGroups.map(group => {
            if (group.id === id) {
                return { ...group, [field]: value };
            }
            return group;
        });
        setFormGroups(updatedGroups);
    };

    const removeGroup = (id) => {
        if (formGroups.length > 1) {
            setFormGroups(formGroups.filter(group => group.id !== id));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (selectedItem !== 0) {
            const element = {}

            languages.forEach(item => {
                element[item.code] = formData[`element${item.code}`] ?? ""
            });

            await dispatch(
                fetchFormElementUpdate({
                    id: selectedItem,
                    file: formData.files,
                    element: element,
                    formId: formGroupId,
                    type: formData.type
                })
            );
        } else {
            for (const group of formGroups) {
                const element = {}

                languages.forEach(item => {
                    element[item.code] = group[`element${item.code}`] ?? ""
                });

                await dispatch(
                    fetchFormElementCreate({
                        file: group.files || [],
                        element: element,
                        formId: formGroupId,
                        type: group.type || 1
                    })
                );
            }
        }

        setFormData(initialFormData);
        setFormGroups([{ ...initialFormData, id: Date.now() }]);
        setChanged(true);
        getData();
        setShowMenu(false);
    }

    const closeModal = () => {
        setFormData(initialFormData);
        setFormGroups([{ ...initialFormData, id: Date.now() }]);
        setShowMenu(!showMenu);
        setSelectedItem(0);
    }

    useEffect(() => { getData() }, [dispatch, selectedItem]);

    const renderForm = () => {
        if (selectedItem !== 0) {
            return (
                <>
                    {languages?.map((item, index) => (
                        <FormItem
                            key={index}
                            id={`element${item?.code}`}
                            name={`element${item?.code}`}
                            type="text"
                            icon={<MdTitle size={20} />}
                            title={`${t("title")} (${item?.code.toUpperCase()})`}
                            formData={formData}
                            setFormData={setFormData}
                            keyT={`element${item?.code}`}
                        />
                    ))}

                    <SelectItem
                        title={t("select_type")}
                        formData={formData}
                        setFormData={setFormData}
                        options={FormTypes}
                        value="type"
                    />
                </>
            );
        } else {
            return (
                <>
                    {formGroups.map((group, groupIndex) => (
                        <Card className="mb-4 shadow-sm" key={group.id}>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h6 className="m-0">{t("form_group")} #{groupIndex + 1}</h6>
                                {formGroups.length > 1 && (
                                    <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeGroup(group.id)}
                                        className="d-flex align-items-center"
                                    >
                                        <BiTrash size={16} className="me-1" /> {t("remove")}
                                    </Button>
                                )}
                            </CardHeader>
                            <CardBody>
                                {languages?.map((item, index) => (
                                    <FormItem
                                        key={`${group.id}-${index}`}
                                        id={`element${item?.code}-${group.id}`}
                                        name={`element${item?.code}`}
                                        type="text"
                                        icon={<MdTitle size={20} />}
                                        title={`${t("title")} (${item?.code.toUpperCase()})`}
                                        formData={group}
                                        setFormData={(updatedData) => {
                                            const updates = {};
                                            Object.keys(updatedData).forEach(key => {
                                                if (key !== 'id' && updatedData[key] !== group[key]) {
                                                    updates[key] = updatedData[key];
                                                }
                                            });

                                            if (Object.keys(updates).length > 0) {
                                                const updatedGroups = formGroups.map(g =>
                                                    g.id === group.id ? { ...g, ...updates } : g
                                                );
                                                setFormGroups(updatedGroups);
                                            }
                                        }}
                                        keyT={`element${item?.code}`}
                                    />
                                ))}

                                <SelectItem
                                    title={t("select_type")}
                                    formData={group}
                                    setFormData={(updatedData) => {
                                        if (updatedData.type !== group.type) {
                                            updateFormGroup(group.id, 'type', updatedData.type);
                                        }
                                    }}
                                    options={FormTypes}
                                    value="type"
                                />
                            </CardBody>
                        </Card>
                    ))}

                    <Button
                        color="info"
                        className="mb-4 d-flex align-items-center mx-auto"
                        onClick={addNewGroup}
                    >
                        <MdPlaylistAdd size={20} className="me-2" /> {t("add_form_group")}
                    </Button>
                </>
            );
        }
    };

    return (
        <div className={showMenu ? 'right-popup' : 'd-none'}>
            <div className='right-popup-close' onClick={closeModal}><BiX size={24} /></div>
            <div className='right-popup-menu'>
                <div className='mb-3 text-center'>
                    <h5>{t(selectedItem === 0 ? "add_form_item" : "edit_form_item")}</h5>
                    {selectedItem === 0 && (
                        <small className="text-muted">
                            {t("multiple_add_description")}
                        </small>
                    )}
                </div>

                <Form onSubmit={handleSubmit}>
                    {renderForm()}

                    <div className='d-flex justify-content-end mt-4 w-100'>
                        <Button type='submit' color={selectedItem === 0 ? 'primary' : 'warning'} className="text-white">
                            {t(selectedItem === 0 ? "add" : "edit")}
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    )
}

export default CrudFormElements